import { useRef, useEffect, useState, useMemo } from 'react';
import { setFeatureState, stylefunction } from 'ol-mapbox-style';
import OLVectorLayer from 'ol/layer/Vector';
import { useSelector } from '@tmapy/redux';
import { STYLE } from '../../style';
import { geoJSONToFeature } from '../../utils/geoJSONFeature';
import { vectorSourceFactory } from '../../utils/vectorSourceFactory';
import { styleFactory, styleFilter } from '../../utils/styleFactory';
import { useOLMap } from '../MapContext';
import { useLayerEffect } from './useLayerEffect';
import { useMapboxSpriteData } from './useMapboxSpriteData';
// feature.id is needet because of mapbox-style - apply.js
const FEATURE_HIGHTLIGHT_ID = '_fakeFeatureHighlightId';
export const vectorLayerById = new Map();
export const VectorLayer = (props) => {
    const map = useOLMap();
    const layerRef = useRef();
    const [layerIds, setLayerIds] = useState({});
    const sourceOptions = useMemo(() => ({ ...props.sourceOptions, layerId: props.id }), [props.sourceOptions, props.id]);
    const mapboxSpriteData = useMapboxSpriteData(props.mapboxStyle?.sprite || '');
    const mapboxSetState = useSelector((state) => state.mapCore.interaction.mapboxFeatureState);
    useLayerEffect(layerRef, props.layerOptions);
    useEffect(() => {
        // set nonOL attribute for redux
        sourceOptions.layerId = props.id;
        let style = null;
        if (props.style && !Array.isArray(props.style)) {
            // Podmince vyhovi vrstvy definovane v configu.
            // Vice by se mi libilo, kdyby vysledek styleFactory prisel rovnou jako props,
            // ale nevim, jak premluvit LayerProps, aby neprotestoval.
            style = styleFactory(props.style);
        }
        const layer = new OLVectorLayer({
            ...{ id: props.id },
            visible: props.layerOptions.visible,
            opacity: props.layerOptions.opacity ?? 1,
            minResolution: props.layerOptions.minResolution,
            maxResolution: props.layerOptions.maxResolution,
            zIndex: props.layerOptions.zIndex,
            source: vectorSourceFactory(sourceOptions),
            style: style ?? props.style ?? STYLE.DEFAULT,
        });
        layerRef.current = layer;
        vectorLayerById.set(props.id, layer);
        if (props.mapboxStyle) {
            const sourceOrLayers = Object.keys(props.mapboxStyle.sources);
            stylefunction(layer, props.mapboxStyle, sourceOrLayers, undefined, mapboxSpriteData.data, mapboxSpriteData.imageUrl);
        }
        map.addLayer(layer);
        if (process.env.NODE_ENV === 'development') {
            console.debug('new VectorLayer', map, layerRef.current);
        }
        return () => {
            // unmount
            const vectorLayer = layerRef.current;
            if (vectorLayer) {
                vectorSourceFactory.map.delete(sourceOptions);
                vectorLayerById.delete(props.id);
                map.removeLayer(layer);
            }
        };
    }, [map, sourceOptions, props.id, mapboxSpriteData]);
    useEffect(() => {
        const layerId = props.features.reduce((accumulator, currentValue) => {
            const layerId = currentValue.properties?.layerId;
            if (!layerId) {
                console.error('[VectorLayer] - GeoJSONFeature has no LayerId');
            }
            else {
                accumulator[layerId] = true;
            }
            return accumulator;
        }, {});
        setLayerIds(layerId);
    }, [props.features]);
    // keep sync with redux state
    useEffect(() => {
        if (!layerIds[props.id])
            return;
        const olFeatures = props.features.map(geoJSONToFeature);
        const vectorLayer = layerRef.current;
        if (vectorLayer) {
            const currentInstanceOLFeatures = vectorLayer.getSource()?.getFeatures();
            if (!currentInstanceOLFeatures)
                return;
            const combineInstanceFeatures = olFeatures.map((newFeature) => {
                const currentInstanceFeature = currentInstanceOLFeatures.find((cf) => (newFeature.get('id') && newFeature.get('id') === cf.get('id')) ||
                    (newFeature.getId() && newFeature.getId() === cf.getId()));
                if (currentInstanceFeature) {
                    currentInstanceFeature.setProperties(newFeature.getProperties());
                    currentInstanceFeature.setGeometry(newFeature.getGeometry());
                    return currentInstanceFeature;
                }
                return newFeature;
            });
            // PERF: v pripade problemu vymyslet jiny zpusob nahrazovani feature
            const source = vectorSourceFactory(sourceOptions);
            source.clear();
            source.addFeatures(combineInstanceFeatures);
        }
    }, [props.features, props.id, sourceOptions, layerIds]);
    useEffect(() => {
        // user defined mapbox styles
        let mapboxStyleFunction;
        const layer = layerRef.current;
        if (!layer)
            return;
        if (props.mapboxStyle) {
            const sourceOrLayers = Object.keys(props.mapboxStyle.sources);
            mapboxStyleFunction = stylefunction(layer, props.mapboxStyle, sourceOrLayers, undefined, mapboxSpriteData.data, mapboxSpriteData.imageUrl);
        }
        const highlightedFeatureIds = new Set(props.highlightedFeatureIds);
        const style = styleFilter((feature) => highlightedFeatureIds.has((feature.getId() ?? feature.get('id'))), styleFactory(props.highlightStyle ?? 'HIGHLIGHT'), mapboxStyleFunction ?? styleFactory(props.style ?? 'DEFAULT'));
        // feature.getId() is needet because of mapbox-style - stylefunction.js
        const styleFunctionWithFixedFeature = (feature, resolution) => {
            if (!feature.getId()) {
                feature.id_ = feature.get('id') ?? FEATURE_HIGHTLIGHT_ID;
            }
            const styleWithZIndex = style(feature, resolution);
            styleWithZIndex?.forEach((style) => style.setZIndex(Infinity));
            return styleWithZIndex;
        };
        layer.setStyle(styleFunctionWithFixedFeature);
    }, [
        props.mapboxStyle,
        props.highlightedFeatureIds,
        props.style,
        props.highlightStyle,
        mapboxSpriteData,
    ]);
    // SET MAPBOX FEATURE STATE
    useEffect(() => {
        if (!props.features.length || !layerRef?.current)
            return;
        const layer = layerRef.current.get('id') === mapboxSetState.layerId ? layerRef.current : null;
        const feature = layer
            ?.getSource()
            ?.getFeatures()
            .find((feature) => feature.getId() === mapboxSetState.featureId ||
            feature.get('id') === mapboxSetState.featureId);
        if (layer && feature) {
            setFeatureState(layer, { id: feature.getId() ?? feature.get('id'), source: '' }, mapboxSetState.state);
        }
    }, [mapboxSetState, props.features]);
    return null;
};
//# sourceMappingURL=VectorLayer.js.map